"use client";

import * as React from "react";
import useEmblaCarousel, {
	type UseEmblaCarouselType,
} from "embla-carousel-react";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

/**
 * Whether an arrow stays on screen once it has run out of slides to scroll to.
 *
 * - `"always"` keeps both arrows mounted, disabling the one that cannot scroll.
 * - `"available"` unmounts that one instead, so a carousel whose content already fits shows no
 *   arrows at all.
 */
type CarouselArrowVisibility = "always" | "available";

type CarouselOrientation = "horizontal" | "vertical";

/**
 * Where an arrow sits relative to the slides.
 *
 * - `"outside"` parks it clear of the viewport, so it never covers content.
 * - `"overlay"` floats it over the slides at the edge it scrolls towards, for a carousel that has
 *   no room beside it to give away. Pair with {@link CarouselFade} so the arrow has something to
 *   sit against.
 */
type CarouselArrowPlacement = "outside" | "overlay";

type CarouselProps = {
	opts?: CarouselOptions;
	plugins?: CarouselPlugin;
	orientation?: CarouselOrientation;
	setApi?: (api: CarouselApi) => void;
	arrowVisibility?: CarouselArrowVisibility;
	arrowPlacement?: CarouselArrowPlacement;
};

type CarouselContextProps = {
	carouselRef: ReturnType<typeof useEmblaCarousel>[0];
	api: ReturnType<typeof useEmblaCarousel>[1];
	scrollPrev: () => void;
	scrollNext: () => void;
	canScrollPrev: boolean;
	canScrollNext: boolean;
	arrowVisibility: CarouselArrowVisibility;
	arrowPlacement: CarouselArrowPlacement;
	// `plugins` and `setApi` are deliberately absent: they configure Embla at mount and are never
	// forwarded to the context, so exposing them here would promise consumers a value that is
	// always undefined.
} & Pick<CarouselProps, "opts" | "orientation">;

/** Controls that need the arrow keys for themselves, to move a caret or change a value. */
const ARROW_KEY_CONSUMERS =
	"input, textarea, select, [role='textbox'], [role='slider'], [role='spinbutton']";

function ownsArrowKeys(target: EventTarget | null) {
	return (
		target instanceof HTMLElement &&
		(target.isContentEditable || target.closest(ARROW_KEY_CONSUMERS) !== null)
	);
}

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

const SCROLL_SNAPSHOTS = [
	{ canScrollPrev: false, canScrollNext: false },
	{ canScrollPrev: true, canScrollNext: false },
	{ canScrollPrev: false, canScrollNext: true },
	{ canScrollPrev: true, canScrollNext: true },
] as const;

function scrollSnapshot(
	canScrollPrev: boolean,
	canScrollNext: boolean,
): (typeof SCROLL_SNAPSHOTS)[number] {
	return (
		SCROLL_SNAPSHOTS.find(
			(snapshot) =>
				snapshot.canScrollPrev === canScrollPrev &&
				snapshot.canScrollNext === canScrollNext,
		) ?? SCROLL_SNAPSHOTS[0]
	);
}

function useCarousel() {
	const context = React.useContext(CarouselContext);

	if (!context) {
		throw new Error("useCarousel must be used within a <Carousel />");
	}

	return context;
}

function Carousel({
	orientation = "horizontal",
	opts,
	setApi,
	plugins,
	arrowVisibility = "always",
	arrowPlacement = "outside",
	className,
	children,
	...props
}: React.ComponentProps<"div"> & CarouselProps) {
	const [carouselRef, api] = useEmblaCarousel(
		{
			...opts,
			axis: orientation === "horizontal" ? "x" : "y",
		},
		plugins,
	);
	const subscribeToScrollState = React.useCallback(
		(onStoreChange: () => void) => {
			if (!api) {
				return () => {};
			}
			const notify = () => onStoreChange();
			api.on("reInit", notify);
			api.on("select", notify);
			return () => {
				api.off("reInit", notify);
				api.off("select", notify);
			};
		},
		[api],
	);

	const scrollState = React.useSyncExternalStore(
		subscribeToScrollState,
		() => {
			const canScrollPrev = api?.canScrollPrev() ?? false;
			const canScrollNext = api?.canScrollNext() ?? false;
			return scrollSnapshot(canScrollPrev, canScrollNext);
		},
		() => scrollSnapshot(false, false),
	);

	const { canScrollPrev, canScrollNext } = scrollState;

	const scrollPrev = React.useCallback(() => {
		api?.scrollPrev();
	}, [api]);

	const scrollNext = React.useCallback(() => {
		api?.scrollNext();
	}, [api]);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			// Capture runs ahead of the slide's own content, so without this an input inside a slide
			// would never receive the keystroke that moves its caret.
			if (ownsArrowKeys(event.target)) {
				return;
			}

			if (event.key === "ArrowLeft") {
				event.preventDefault();
				scrollPrev();
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				scrollNext();
			}
		},
		[scrollPrev, scrollNext],
	);

	React.useEffect(() => {
		if (!api || !setApi) {
			return;
		}
		setApi(api);
	}, [api, setApi]);

	return (
		<CarouselContext.Provider
			value={{
				carouselRef,
				api,
				opts,
				orientation,
				scrollPrev,
				scrollNext,
				canScrollPrev,
				canScrollNext,
				arrowVisibility,
				arrowPlacement,
			}}
		>
			<div
				onKeyDownCapture={handleKeyDown}
				className={cn("relative", className)}
				role="region"
				aria-roledescription="carousel"
				data-slot="carousel"
				{...props}
			>
				{children}
			</div>
		</CarouselContext.Provider>
	);
}

function CarouselContent({ className, ...props }: React.ComponentProps<"div">) {
	const { carouselRef, orientation } = useCarousel();

	return (
		<div
			ref={carouselRef}
			className="overflow-hidden"
			data-slot="carousel-content"
			// The viewport is not addressable through a prop — `props` go to the track
			// inside it — so it carries a fixed test id for the tests that assert it.
			data-testid="carousel-content"
		>
			<div
				className={cn(
					"flex",
					orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
					className,
				)}
				{...props}
			/>
		</div>
	);
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
	const { orientation } = useCarousel();

	return (
		<div
			role="group"
			aria-roledescription="slide"
			data-slot="carousel-item"
			className={cn(
				"min-w-0 shrink-0 grow-0 basis-full",
				orientation === "horizontal" ? "pl-4" : "pt-4",
				className,
			)}
			{...props}
		/>
	);
}

/** Which end of the track a {@link CarouselFade} covers, in the axis's own terms. */
type CarouselFadeSide = "start" | "end";

/**
 * The gradient each fade is drawn with, for every orientation and side.
 *
 * Each one runs from the page colour at the edge to transparent inwards, so the slides appear to
 * dissolve into the surface rather than stopping at a cut.
 */
const FADE_POSITION: Record<
	CarouselOrientation,
	Record<CarouselFadeSide, string>
> = {
	horizontal: {
		start: "inset-y-0 left-0 w-16 bg-gradient-to-r",
		end: "inset-y-0 right-0 w-16 bg-gradient-to-l",
	},
	vertical: {
		start: "inset-x-0 top-0 h-16 bg-gradient-to-b",
		end: "inset-x-0 bottom-0 h-16 bg-gradient-to-t",
	},
};

/**
 * A soft edge over the slides at one end of the track, for a carousel whose arrows overlay it.
 *
 * Deliberately its own part rather than something `arrowPlacement="overlay"` draws for you: an
 * overlaid arrow and a faded edge are useful separately, and a carousel on a coloured surface
 * needs to restate the `from-*` colour, which only the call site knows.
 *
 * Sits inside the `Carousel` root but outside `CarouselContent`'s `overflow-hidden`, so it paints
 * over the slides; the `z-10` on an overlaid arrow keeps the arrow above it in turn.
 *
 * @param props.side - Which end to cover, in the scroll axis's own terms.
 */
function CarouselFade({
	side,
	className,
	...props
}: React.ComponentProps<"div"> & {
	side: CarouselFadeSide;
}) {
	const {
		orientation = "horizontal",
		canScrollPrev,
		canScrollNext,
	} = useCarousel();

	// Hidden rather than unmounted, so it eases out as the last slide arrives instead of
	// disappearing on the frame the scroll settles.
	const coversSlides = side === "start" ? canScrollPrev : canScrollNext;

	return (
		<div
			aria-hidden
			data-slot="carousel-fade"
			data-side={side}
			className={cn(
				"pointer-events-none absolute z-[1] from-background to-transparent transition-opacity duration-200",
				FADE_POSITION[orientation][side],
				!coversSlides && "opacity-0",
				className,
			)}
			{...props}
		/>
	);
}

/**
 * Where each arrow sits, for every orientation and placement.
 *
 * Gathered into one table rather than left as ternaries at the two call sites, so the four
 * combinations can be compared at a glance and an overlaid arrow cannot drift out of step with its
 * outside counterpart. Overlaid arrows carry `z-10` to clear a {@link CarouselFade} beneath them.
 */
const ARROW_POSITION: Record<
	CarouselOrientation,
	Record<CarouselArrowPlacement, { previous: string; next: string }>
> = {
	horizontal: {
		outside: {
			previous: "-left-12 top-1/2 -translate-y-1/2",
			next: "-right-12 top-1/2 -translate-y-1/2",
		},
		overlay: {
			previous: "left-2 top-1/2 z-10 -translate-y-1/2",
			next: "right-2 top-1/2 z-10 -translate-y-1/2",
		},
	},
	vertical: {
		outside: {
			previous: "-top-12 left-1/2 -translate-x-1/2 rotate-90",
			next: "-bottom-12 left-1/2 -translate-x-1/2 rotate-90",
		},
		overlay: {
			previous: "top-2 left-1/2 z-10 -translate-x-1/2 rotate-90",
			next: "bottom-2 left-1/2 z-10 -translate-x-1/2 rotate-90",
		},
	},
};

type CarouselArrowProps = React.ComponentProps<typeof Button> & {
	/** Overrides the `arrowVisibility` set on the parent `Carousel`. */
	visibility?: CarouselArrowVisibility;
	/** Overrides the `arrowPlacement` set on the parent `Carousel`. */
	placement?: CarouselArrowPlacement;
};

function CarouselPrevious({
	className,
	variant = "outline",
	size = "icon-sm",
	visibility,
	placement,
	...props
}: CarouselArrowProps) {
	const {
		orientation = "horizontal",
		scrollPrev,
		canScrollPrev,
		arrowVisibility,
		arrowPlacement,
	} = useCarousel();

	if ((visibility ?? arrowVisibility) === "available" && !canScrollPrev) {
		return null;
	}

	return (
		<Button
			data-slot="carousel-previous"
			variant={variant}
			size={size}
			className={cn(
				"absolute rounded-full",
				ARROW_POSITION[orientation][placement ?? arrowPlacement].previous,
				className,
			)}
			disabled={!canScrollPrev}
			onClick={scrollPrev}
			{...props}
		>
			<ArrowLeftIcon />
			<span className="sr-only">Previous slide</span>
		</Button>
	);
}

function CarouselNext({
	className,
	variant = "outline",
	size = "icon-sm",
	visibility,
	placement,
	...props
}: CarouselArrowProps) {
	const {
		orientation = "horizontal",
		scrollNext,
		canScrollNext,
		arrowVisibility,
		arrowPlacement,
	} = useCarousel();

	if ((visibility ?? arrowVisibility) === "available" && !canScrollNext) {
		return null;
	}

	return (
		<Button
			data-slot="carousel-next"
			variant={variant}
			size={size}
			className={cn(
				"absolute rounded-full",
				ARROW_POSITION[orientation][placement ?? arrowPlacement].next,
				className,
			)}
			disabled={!canScrollNext}
			onClick={scrollNext}
			{...props}
		>
			<ArrowRightIcon />
			<span className="sr-only">Next slide</span>
		</Button>
	);
}

export {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselFade,
	CarouselPrevious,
	CarouselNext,
	useCarousel,
	type CarouselApi,
	type CarouselArrowVisibility,
	type CarouselArrowPlacement,
	type CarouselFadeSide,
};
